import { ChevronRight } from "lucide-react";

import { cn } from "../../lib/utils";

interface Props {
  readonly description: string | null | undefined;
  readonly className?: string;
}

/** Inline expandable description for table rows / list items.
 *
 *  Vehicle deposits (and any other ``initialDescription`` from a deep
 *  link) come in as multi-line ``\n``-separated blurbs. Without
 *  expansion the row would either clip everything past the first
 *  line (Year/Make/Model) - the cashier never sees VIN / Engine /
 *  Miles - or eat several rows of vertical space.
 *
 *  Behaviour:
 *  - empty / nullish → renders nothing
 *  - short single-line (≤ 60 chars, no ``\n``) → truncated div, no
 *    expander affordance (keeps simple rows simple)
 *  - multi-line OR long → ``<details>`` panel; collapsed shows a
 *    chevron + first line truncated; expanded shows the full
 *    ``whitespace-pre-line`` content
 *
 *  Click on the summary stops propagation so a parent ``<tr onClick>``
 *  / ``<Link>`` row navigation isn't triggered when the cashier just
 *  wants to peek at the description. */
export function DescriptionDetails({ description, className }: Props) {
  const trimmed = description?.trim();
  if (!trimmed) return null;

  const isMultiline = trimmed.includes("\n");
  const isLong = trimmed.length > 60;

  if (!isMultiline && !isLong) {
    return <div className={cn("truncate", className)}>{trimmed}</div>;
  }

  const firstLine = trimmed.split("\n")[0] ?? trimmed;

  return (
    <details className={cn("group min-w-0", className)}>
      <summary
        onClick={(e) => e.stopPropagation()}
        className="flex cursor-pointer list-none items-start gap-1 [&::-webkit-details-marker]:hidden"
      >
        <ChevronRight
          className="mt-[0.2em] h-3 w-3 shrink-0 text-faint transition-transform group-open:rotate-90"
          aria-hidden="true"
        />
        <span className="block min-w-0 flex-1 truncate group-open:hidden">{firstLine}</span>
        <span className="hidden min-w-0 flex-1 whitespace-pre-line group-open:block">
          {trimmed}
        </span>
      </summary>
    </details>
  );
}
